import { useEffect } from 'react';
import { commonEventListenerOptions } from '../hooks';

/**
 * Hook for dismissing popups
 *
 * This hook will call the given callback `onDismiss` when the user clicks
 * outside the given element, or presses the Esc key.
 *
 * Note that the clicks are monitored using window.addEventListener, so they
 * will not be handled as React synthetic events.
 *
 * @param element Element to monitor for outside clicks.
 * @param onDismiss Callback that should be called when dismissal is requested.
 *
 * @internal
 */

export var useDismiss = function useDismiss(element, onDismiss) {
  useEffect(function () {
    if (!element) {
      return;
    }
    var clickEventListener = function clickEventListener(event) {
      // Dismiss if click target is not a descendant of the element being monitored.
      if (event.target instanceof Element && element && !element.contains(event.target)) {
        onDismiss();
      }
    };
    var keyDownEventListener = function keyDownEventListener(event) {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };
    window.addEventListener('click', clickEventListener, commonEventListenerOptions);
    window.addEventListener('keydown', keyDownEventListener, commonEventListenerOptions);
    return function () {
      window.removeEventListener('click', clickEventListener, commonEventListenerOptions);
      window.removeEventListener('keydown', keyDownEventListener, commonEventListenerOptions);
    };
  }, [element, onDismiss]);
};